import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  CustomTableFilterQueryKey,
  fetchCustomTableFilterData,
} from '../../api/portalGateway/customTableFilterData';
import {
  fetchTableFilterData,
  TableFilterQueryKey,
} from '../../api/portalGateway/tableFilterData';
import { useStore } from '../../store';
import { useEffectiveGlobalSelections } from '../../store/hooks/useEffectiveGlobalSelections';
import { useSelections } from '../../store/hooks/useSelections';
import { FilterConfigDto, FilterData } from '../../store/states/filterTypes';
import { CUSTOM_FILTER_INSTANCE_ID_SUFFIX } from '../../utils/constants';
import { prependRoleId } from '../../utils/prependRoleId';

import {
  EMPTY_FILTER_CONFIGS,
  Selection,
  useSanitizeSelections,
  validateFiltersFromResponse,
  validateSingleFilterFromResponse,
} from './filterQueryInternals';
import { useGlobalFilterLoaded } from './useGlobalFilterQuery';

export function useTableFilterQuery(
  widgetId: string,
  tableFilterInstanceId: string,
  pageFilterInstanceId: string,
  filterConfigs: FilterConfigDto[] | undefined
) {
  const globalSelections = useEffectiveGlobalSelections();
  const pageSelections = useSelections(pageFilterInstanceId);
  const customPageFilterInstanceId = `${pageFilterInstanceId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`;
  const customPageSelections = useSelections(customPageFilterInstanceId);
  const tableSelections = useSelections(tableFilterInstanceId);

  const combinedParentSelections: Selection = {
    ...globalSelections,
    ...pageSelections,
    ...customPageSelections,
  };

  const globalFilterLoaded = useGlobalFilterLoaded();

  const pageFilterQueryLoaded = useStore(
    (state) => state.filterQueryLoaded[pageFilterInstanceId] ?? false
  );
  const customPageFilterLoaded = useStore(
    (state) => state.filterQueryLoaded[customPageFilterInstanceId] ?? false
  );

  const hasFilters = filterConfigs != null && filterConfigs.length > 0;

  const query = useQuery({
    queryKey: prependRoleId<TableFilterQueryKey>([
      'tableFilters',
      widgetId,
      combinedParentSelections,
      tableSelections,
    ]),
    queryFn: fetchTableFilterData,
    enabled:
      hasFilters &&
      globalFilterLoaded &&
      pageFilterQueryLoaded &&
      customPageFilterLoaded,
  });

  const isSanitized = useSanitizeSelections(
    query,
    tableFilterInstanceId,
    filterConfigs ?? EMPTY_FILTER_CONFIGS,
    tableSelections,
    (sanitized) =>
      prependRoleId<TableFilterQueryKey>([
        'tableFilters',
        widgetId,
        combinedParentSelections,
        sanitized,
      ])
  );

  const { validatedData, validationError } = useMemo(() => {
    if (query.data == null) {
      return { validatedData: undefined, validationError: undefined };
    }
    try {
      return {
        validatedData: validateFiltersFromResponse(
          filterConfigs ?? [],
          query.data
        ),
        validationError: undefined,
      };
    } catch (error) {
      return { validatedData: undefined, validationError: error };
    }
  }, [query.data, filterConfigs]);

  if (validationError) {
    throw validationError;
  }

  const isSuccess = !hasFilters || (query.isSuccess && isSanitized);

  return {
    error: query.error,
    isFetching: query.isFetching,
    isSuccess,
    validatedData,
  };
}

// ---------------------------------------------------------------------------
// Custom table filter query
// ---------------------------------------------------------------------------

export function useCustomTableFilterQuery(
  widgetId: string,
  tableFilterInstanceId: string,
  pageFilterInstanceId: string,
  activeFilterConfigs: FilterConfigDto[]
) {
  const globalSelections = useEffectiveGlobalSelections();
  const pageSelections = useSelections(pageFilterInstanceId);
  const customPageFilterInstanceId = `${pageFilterInstanceId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`;
  const customPageSelections = useSelections(customPageFilterInstanceId);
  const tableSelections = useSelections(tableFilterInstanceId);

  const combinedParentSelections: Selection = {
    ...globalSelections,
    ...pageSelections,
    ...customPageSelections,
  };

  const pageFilterQueryLoaded = useStore(
    (state) => state.filterQueryLoaded[pageFilterInstanceId] ?? false
  );
  const customPageFilterLoaded = useStore(
    (state) => state.filterQueryLoaded[customPageFilterInstanceId] ?? false
  );

  const globalFilterLoaded = useGlobalFilterLoaded();

  const hasFilters = activeFilterConfigs.length > 0;

  const query = useQuery({
    queryKey: prependRoleId<CustomTableFilterQueryKey>([
      'customQueryFilters',
      widgetId,
      activeFilterConfigs.map((el) => el.key),
      combinedParentSelections,
      tableSelections,
    ]),
    queryFn: fetchCustomTableFilterData,
    enabled:
      hasFilters &&
      globalFilterLoaded &&
      pageFilterQueryLoaded &&
      customPageFilterLoaded,
  });

  const isSanitized = useSanitizeSelections(
    query,
    tableFilterInstanceId,
    activeFilterConfigs,
    tableSelections,
    (sanitized) =>
      prependRoleId<CustomTableFilterQueryKey>([
        'customQueryFilters',
        widgetId,
        activeFilterConfigs.map((el) => el.key),
        combinedParentSelections,
        sanitized,
      ])
  );

  const queryData = query.data;
  const { validatedData, validationError } = useMemo(() => {
    if (queryData == null) {
      return { validatedData: undefined, validationError: undefined };
    }
    try {
      return {
        validatedData: activeFilterConfigs.reduce<Record<string, FilterData>>(
          (acc, config) => {
            acc[config.key] = validateSingleFilterFromResponse(
              config,
              queryData.data[config.key]
            );
            return acc;
          },
          {}
        ),
        validationError: undefined,
      };
    } catch (error) {
      return { validatedData: undefined, validationError: error };
    }
  }, [queryData, activeFilterConfigs]);

  if (validationError) {
    throw validationError;
  }

  const isSuccess = !hasFilters || (query.isSuccess && isSanitized);

  return {
    error: query.error,
    isError: query.isError,
    isFetching: query.isFetching,
    isSuccess,
    validatedData,
  };
}
